/**
 * Analytics configuration
 *
 * Google Ads: Replace AW-XXXXXXXXX with your actual Conversion ID after
 * creating conversion actions in Google Ads.
 */

export const GA4_ID = 'G-8Q9G6BY9JG';
export const CLARITY_ID = 'uf9oc5cour';

// PostHog - Product analytics, funnels, A/B testing
// Set VITE_POSTHOG_API_KEY in .env.production
export const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY || 'phc_NLL4ftu7DWrziT9Q4Qi8pzsAyKFoTfuaT1e0W3h6tJw';

// LinkedIn Insight Tag - Partner ID from LinkedIn Campaign Manager
// Get yours at: Campaign Manager > Account Assets > Insight Tag
export const LINKEDIN_PARTNER_ID = import.meta.env.VITE_LINKEDIN_PARTNER_ID || '';

// Google Ads - Replace with your actual Conversion ID
// Format: AW-XXXXXXXXX
export const GOOGLE_ADS_ID = 'AW-17780484339';

// Google Ads Conversion Labels
// Replace these after creating conversion actions in Google Ads
export const CONVERSIONS = {
  SIGN_UP: {
    id: GOOGLE_ADS_ID,
    label: '8AaHCPzMn9kbEPPRsp5C',
  },
  PURCHASE: {
    id: GOOGLE_ADS_ID,
    label: 'MtckCIHbjMwbEPPRsp5C',
  },
  TRIAL_STARTED: {
    id: GOOGLE_ADS_ID,
    // trial_signup conversion action (PRIMARY, bid-optimizing - see
    // docs/analytics/ads-measurement-stack.md). Created 2026-07-06.
    label: 'Ro3nCM2xtsscEPPRsp5C',
  },
  FIRST_REPO_IMPORTED: {
    id: GOOGLE_ADS_ID,
    label: 'F3CyCIa1mtkbEPPRsp5C',
  },
  QUIZ_LEAD: {
    id: GOOGLE_ADS_ID,
    label: 'aChjCPnTqesbEPPRsp5C',
  },
} as const;

// GA4 event name that trackConversion() emits for each conversion type.
// A distinctly-named event per type lets GA4 build clean funnel steps and key
// events (the old code fired one generic `conversion` event for everything,
// which GA4 could not split into funnel stages).
//
// `null` = this conversion already fires as a first-class GA4 event elsewhere,
// so trackConversion() must NOT emit it again or GA4 would double-count:
//   - SIGN_UP  -> `sign_up` is tracked in CallbackPage
//   - PURCHASE -> `purchase` is the ecommerce event fired by trackPurchase()
// The Google Ads and PostHog paths are unaffected (separate calls).
export const CONVERSION_GA4_EVENT: Record<keyof typeof CONVERSIONS, string | null> = {
  SIGN_UP: null,
  PURCHASE: null,
  TRIAL_STARTED: 'trial_started',
  FIRST_REPO_IMPORTED: 'first_repo_imported',
  QUIZ_LEAD: 'quiz_lead',
};

// Event names - centralized for consistency
export const EVENTS = {
  // Auth events
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  LOGOUT: 'logout',
  ACCOUNT_SWITCH_INITIATED: 'account_switch_initiated',

  // Onboarding events
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_BLOCKED: 'onboarding_blocked',
  ONBOARDING_ORGS_VIEWED: 'onboarding_orgs_viewed',
  ONBOARDING_ORGS_CONTINUED: 'onboarding_orgs_continued',
  ONBOARDING_PERSONAL_ONLY_CLICKED: 'onboarding_personal_only_clicked',
  ONBOARDING_PREREQUISITES_VIEWED: 'onboarding_prerequisites_viewed',
  ONBOARDING_PREREQUISITES_CONTINUED: 'onboarding_prerequisites_continued',
  ONBOARDING_ORG_SELECT_VIEWED: 'onboarding_org_select_viewed',
  ONBOARDING_NO_ORGS_SHOWN: 'onboarding_no_orgs_shown',
  ONBOARDING_REPO_SELECT_VIEWED: 'onboarding_repo_select_viewed',
  ONBOARDING_REPO_SKIPPED: 'onboarding_repo_skipped',
  // Diagnostics for the repo-select -> import drop-off (pinpoint WHY users who
  // reach repo-select never import: empty list, load error, deselected all, or
  // cancelled at the no-PR-workflow warning).
  ONBOARDING_REPO_EMPTY_SHOWN: 'onboarding_repo_empty_shown',
  ONBOARDING_REPO_LOAD_ERROR: 'onboarding_repo_load_error',
  ONBOARDING_REPO_ALL_DESELECTED: 'onboarding_repo_all_deselected',
  ONBOARDING_WORKFLOW_WARNING_CANCELLED: 'onboarding_workflow_warning_cancelled',
  ONBOARDING_INSTALL_REQUEST_PENDING: 'onboarding_install_request_pending',
  // Fired when the "check if approved" poll detects the install landed.
  ONBOARDING_INSTALL_APPROVAL_DETECTED: 'onboarding_install_approval_detected',
  ORG_SELECTED: 'org_selected',
  ORG_IMPORTED: 'org_imported',
  REPOS_IMPORTED: 'repos_imported',
  IMPORT_ERROR: 'import_error',
  IMPORT_ATTEMPTED: 'import_attempted',
  INSTALL_APPROVAL_REQUIRED: 'install_approval_required',
  ONBOARDING_INSTALL_VIEWED: 'onboarding_install_viewed',
  ONBOARDING_MOBILE_DESKTOP_LINK_REQUESTED: 'onboarding_mobile_desktop_link_requested',
  ONBOARDING_ADMIN_INVITE_SENT: 'onboarding_admin_invite_sent',
  // Fired when someone explicitly chooses to continue without a work org at the
  // recovery screen. Durable server-side, so this should be a one-per-user event.
  ONBOARDING_PERSONAL_ACCOUNT_ACCEPTED: 'onboarding_personal_account_accepted',
  ONBOARDING_PERSONALIZATION_VIEWED: 'onboarding_personalization_viewed',
  ONBOARDING_PERSONALIZATION_COMPLETED: 'onboarding_personalization_completed',
  ONBOARDING_SYNCING_VIEWED: 'onboarding_syncing_viewed',
  // Fired when the FIRST repo finishes quick-sync (data becomes usable) - the
  // funnel step between trial_started and begin_checkout.
  SYNC_COMPLETED: 'sync_completed',
  ONBOARDING_INSIGHTS_VIEWED: 'onboarding_insights_viewed',
  PERSONAL_ACCOUNT_SELECTED: 'personal_account_selected',

  // Conversion events
  BEGIN_CHECKOUT: 'begin_checkout',
  PURCHASE: 'purchase',
  TRIAL_STARTED: 'trial_started',

  // Feature usage events
  PAGE_VIEW: 'page_view',
  DASHBOARD_VIEW: 'dashboard_view',
  DEVELOPER_VIEW: 'developer_view',
  REPOSITORY_VIEW: 'repository_view',
  FEATURE_USED: 'feature_used',
  EXPORT_CSV: 'export_csv',
  ALERT_CREATED: 'alert_created',

  // Engagement events
  CTA_CLICK: 'cta_click',
  // Fired on cal.com demo CTA clicks; the GA4 key event imported into Google
  // Ads as the secondary conversion.
  DEMO_CLICK: 'demo_click',
  SCROLL_DEPTH: 'scroll_depth',
  FAQ_EXPANDED: 'faq_expanded',
  // Emitted once per page-load on the first pointer/key/touch/scroll. PostHog
  // runs with autocapture off, so this is the ONLY event a mail-scanner or
  // prefetcher cannot produce - count it, not $pageview, when asking how many
  // people a campaign actually brought. See analytics/engagement.ts.
  ENGAGED_SESSION: 'engaged_session',

  // Time period events
  TIME_PERIOD_CHANGED: 'time_period_changed',

  // Share events
  SHARE_CREATED: 'share_created',
  SHARE_LINK_COPIED: 'share_link_copied',
  SHARE_IMAGE_DOWNLOADED: 'share_image_downloaded',
  // Social/content sharing (tool & content ShareButtons) - distinct from the
  // report ShareModal token flow above. `channel` = copy|linkedin|twitter|native.
  CONTENT_SHARED: 'content_shared',

  // Upgrade events
  UPGRADE_MODAL_VIEW: 'upgrade_modal_view',
  FEATURE_GATE_CLICK: 'feature_gate_click',

  // Subscription events
  CANCELLATION_STARTED: 'cancellation_started',
  CANCELLATION_REASON_SELECTED: 'cancellation_reason_selected',
  CANCELLATION_FEEDBACK_SUBMITTED: 'cancellation_feedback_submitted',
  RETENTION_OFFER_SHOWN: 'retention_offer_shown',
  RETENTION_OFFER_ACCEPTED: 'retention_offer_accepted',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  SUBSCRIPTION_PAUSED: 'subscription_paused',
  SUBSCRIPTION_RESUMED: 'subscription_resumed',

  // End-of-trial decision page events (Section B, pre-ads funnel readiness).
  // Values MUST equal lowercase(key) so the test-setup EVENTS proxy resolves
  // to the same strings as production.
  TRIAL_ENDED_SCREEN_VIEWED: 'trial_ended_screen_viewed',
  TRIAL_ENDED_EXTEND_CLICKED: 'trial_ended_extend_clicked',
  TRIAL_ENDED_PLAN_CLICKED: 'trial_ended_plan_clicked',
  TRIAL_ENDED_FREE_CHOSEN: 'trial_ended_free_chosen',

  // Feedback events
  FEEDBACK_PROMPT_SHOWN: 'feedback_prompt_shown',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
  FEEDBACK_DISMISSED: 'feedback_dismissed',
  FEEDBACK_REQUESTED_HELP: 'feedback_requested_help',

  // Experiment events
  EXPERIMENT_EXPOSURE: 'experiment_exposure',

  // Organization access check events
  ONBOARDING_ORG_ACCESS_CHECK_VIEWED: 'onboarding_org_access_check_viewed',
  ONBOARDING_ORG_ACCESS_CONFIRMED: 'onboarding_org_access_confirmed',
  ONBOARDING_ORG_ACCESS_DENIED: 'onboarding_org_access_denied',
  ONBOARDING_GRANT_ACCESS_CLICKED: 'onboarding_grant_access_clicked',

  // Quiz lead magnet events
  QUIZ_STARTED: 'quiz_started',
  QUIZ_COMPLETED: 'quiz_completed',
  QUIZ_EMAIL_CAPTURED: 'quiz_email_captured',
} as const;
